import type * as Highcharts from 'highcharts';
import dayjs from 'dayjs';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import { plotLines } from '../../constants';
import type { CalendarSeries, DateValue } from '../../types';
import { getYearlyDaysFromDate } from '../utils';

dayjs.extend(dayOfYear);

const PLOT_LINES: Record<string, { label?: string; name?: string; color: string }> = plotLines;

/** Daily series shown on the typical soiling year; the mass rate is a debug series of the development environments. */
export const TYPICAL_YEAR_LINES = ['local', 'int'].includes(import.meta.env.VITE_ENVIRONMENT)
  ? ['soiling_ratio', 'rain', 'soiling_mass_rate']
  : ['soiling_ratio', 'rain'];

export interface TypicalSoilingYearParams {
  yearlyCleaningDays: DateValue[] | undefined;
  rainThreshold: number | undefined;
  ymin: number;
  ymax: number;
}

const typicalYearYAxis = (key: string, { rainThreshold, ymin, ymax }: TypicalSoilingYearParams): Highcharts.YAxisOptions => ({
  title: { text: PLOT_LINES[key].label },
  opposite: key !== 'soiling_ratio',
  plotLines:
    key === 'rain'
      ? [{ label: { text: plotLines.rain_threshold.label }, value: rainThreshold, width: 2, color: plotLines.rain_threshold.color }]
      : [],
  ...(key === 'soiling_ratio' ? { max: ymax, min: ymin, tickInterval: 0.2, startOnTick: true, endOnTick: true, allowDecimals: true } : {}),
});

/** Daily soiling ratio and rain of the typical year, with the yearly cleaning days and the rain threshold. */
export const buildTypicalSoilingYearOptions = (soiling: CalendarSeries, params: TypicalSoilingYearParams): Highcharts.Options => {
  const keys = Object.keys(soiling.daily?.values).filter((k) => TYPICAL_YEAR_LINES.includes(k));

  return {
    chart: { type: 'line', zooming: { type: 'x' } },
    title: { text: 'Typical Soiling Year' },
    xAxis: {
      categories: soiling.daily?.times,
      title: { text: 'Date' },
      labels: {
        formatter: function () {
          const d = new Date(Date.parse(String(this.value)));
          return d.getDate() + '/' + ('0' + (d.getMonth() + 1)).slice(-2);
        },
      },
      gridLineWidth: 1,
      plotLines: getYearlyDaysFromDate(params.yearlyCleaningDays as DateValue[]).map((val) => ({
        label: { text: 'cleaning' },
        color: 'green',
        value: dayjs(val).dayOfYear() - 1,
        width: 2,
      })),
    },
    yAxis: keys.map((key) => typicalYearYAxis(key, params)),
    legend: { enabled: true },
    tooltip: { valueDecimals: 4, shared: true },
    credits: { enabled: false },
    plotOptions: { area: { stacking: 'normal', marker: { enabled: false } } },
    series: keys.map(
      (key, index) =>
        ({
          name: PLOT_LINES[key].name,
          type: key === 'rain' ? 'column' : 'line',
          color: PLOT_LINES[key].color,
          data: soiling.daily?.values[key],
          yAxis: index,
        }) as Highcharts.SeriesOptionsType,
    ),
  };
};
